import { useEffect, useState } from "react";
import { FieldValues, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import useFetch from "../../../hooks/useFetch";
import { getAllPrices } from "../../../services/Pricing.service";
import PricingTable from "./PricingTable";
import SearchTrip from "./SearchTrip";

export type Inputs = {
  tripId: number;
  ageGroupId: number;
  lineId: number;
  defaultCurrencyId?: number;
};

const Prices: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [tripParams, setTripParams] = useState<Inputs>({
    tripId: 0,
    ageGroupId: 0,
    lineId: 0,
    defaultCurrencyId: undefined
  });

  const {
    response: oneWayResponse,
    handleFunc: fetchOneWay,
    loading: loadingOneWay
  } = useFetch(getAllPrices, { initRequest: false });

  const {
    response: roundTripResponse,
    handleFunc: fetchRoundTrip,
    loading: loadingRoundTrip
  } = useFetch(getAllPrices, { initRequest: false });

  const loading = loadingOneWay || loadingRoundTrip;

  const methods = useForm<Inputs>();
  const { setValue } = methods;

  const fetchPrices = (params: Inputs) => {
    void fetchOneWay({
      tripId: params.tripId,
      ageGroupId: params.ageGroupId,
      lineId: params.lineId,
      isTwoWayTrip: false
    });
    void fetchRoundTrip({
      tripId: params.tripId,
      ageGroupId: params.ageGroupId,
      lineId: params.lineId,
      isTwoWayTrip: true
    });
  };

  useEffect(() => {
    if (
      searchParams.has("lineId") &&
      searchParams.has("tripId") &&
      searchParams.has("tripTime")
    ) {
      const lineId = Number(searchParams.get("lineId"));
      const tripId = Number(searchParams.get("tripId"));
      const ageGroupId = 1;
      const defaultCurrencyId =
        Number(searchParams.get("defaultCurrencyId")) || undefined;

      setValue("lineId", lineId);
      setValue("tripId", tripId);
      setValue("ageGroupId", ageGroupId);
      if (defaultCurrencyId) setValue("defaultCurrencyId", defaultCurrencyId);

      if (lineId && tripId && ageGroupId) {
        const params = { lineId, tripId, ageGroupId, defaultCurrencyId };
        setTripParams(params);
        fetchPrices(params);
      }
    }
  }, [searchParams, setValue]);

  const handlePricingSubmit = (data: FieldValues) => {
    const tripId = Number(data.tripId);
    const ageGroupId = Number(data.ageGroupId);
    const lineId = Number(data.lineId);
    const defaultCurrencyId = Number(data.defaultCurrencyId) || undefined;

    if (!tripId || !ageGroupId || !lineId) return;

    const params = { tripId, ageGroupId, lineId, defaultCurrencyId };
    fetchPrices(params);
    setTripParams(params);
  };

  const onSubmit: SubmitHandler<FieldValues> = handlePricingSubmit;

  return (
    <>
      <FormProvider {...methods}>
        <SearchTrip onSubmit={onSubmit} />
        <div
          className={`relative ${loading ? "opacity-50" : "-mx-3 md:mx-0"}`}
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              Duke u mbushur të dhënat...
            </div>
          )}
          {!loading && (
            <PricingTable
              oneWayData={oneWayResponse?.data}
              roundTripData={roundTripResponse?.data}
              tripParams={tripParams}
            />
          )}
        </div>
      </FormProvider>
    </>
  );
};

export default Prices;
